// Month-range labels for a quarterly revision: which months are actual
// sales and which are still plan. Every column header that shows one of the
// two carries its months, so nobody has to remember what a number covers.
const fmtMonth = (iso: string) => new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })

// Both years are printed when the range crosses a year boundary, so
// "Oct 2026 – Jun 2027" can never be read as ten months of one year.
export function monthRangeLabel(startIso: string, endIso: string): string {
  const start = new Date(startIso + 'T00:00:00')
  const end = new Date(endIso + 'T00:00:00')
  if (start.getFullYear() !== end.getFullYear()) {
    return `${fmtMonth(startIso)} ${start.getFullYear()} – ${fmtMonth(endIso)} ${end.getFullYear()}`
  }
  return `${fmtMonth(startIso)} – ${fmtMonth(endIso)} ${end.getFullYear()}`
}

// The same range as numbers, for the narrow "Actual" column header:
// "01-09/2026", or "07/2026-03/2027" when the range crosses a year boundary.
export function numericMonthRangeLabel(startIso: string, endIso: string): string {
  const [startYear, startMonth] = startIso.split('-')
  const [endYear, endMonth] = endIso.split('-')
  if (startYear !== endYear) return `${startMonth}/${startYear}-${endMonth}/${endYear}`
  return `${startMonth}-${endMonth}/${endYear}`
}

// The calendar quarters a range covers, for the remaining-months target
// header: "Q4", "Q3-Q4", or "Q4 2026-Q2 2027" across a year boundary.
export function quarterRangeLabel(startIso: string, endIso: string): string {
  const [startYear, startMonth] = startIso.split('-').map(Number)
  const [endYear, endMonth] = endIso.split('-').map(Number)
  const startQ = `Q${Math.floor((startMonth - 1) / 3) + 1}`
  const endQ = `Q${Math.floor((endMonth - 1) / 3) + 1}`
  if (startYear !== endYear) return `${startQ} ${startYear}-${endQ} ${endYear}`
  return startQ === endQ ? startQ : `${startQ}-${endQ}`
}

// Last day before the plan period, as ISO.
function dayBefore(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  d.setDate(d.getDate() - 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// `past` names the comparison window the page's past-revenue numbers come
// from (the viewer's pick, by default the plan period one year earlier), so
// the "Last year" column header carries the months it really shows.
export function revisionLabels(
  planPeriod: { start: string; end: string } | undefined,
  actualsMonths: number | null | undefined,
  beginDate: string,
  comparePeriod: { start: string; end: string },
): {
  actual: string
  actualNumeric: string
  actualQuarters: string
  plan: string
  quarters: string
  past: string
  months: number
} | null {
  if (!planPeriod || !actualsMonths) return null
  const actualsEnd = dayBefore(planPeriod.start)
  return {
    actual: monthRangeLabel(beginDate, actualsEnd),
    actualNumeric: numericMonthRangeLabel(beginDate, actualsEnd),
    actualQuarters: quarterRangeLabel(beginDate, actualsEnd),
    plan: monthRangeLabel(planPeriod.start, planPeriod.end),
    quarters: quarterRangeLabel(planPeriod.start, planPeriod.end),
    past: monthRangeLabel(comparePeriod.start, comparePeriod.end),
    months: actualsMonths,
  }
}
